"use client"

import type React from "react"
import { useState, useCallback } from "react"
import { X } from "lucide-react"
import type { DynamicUser, DynamicSchema } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import { updateDynamicUserByUniqueField } from "../services/api"
import { addToQueue } from "../services/queue"
import { updateDynamicUserInDB } from "../services/database"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { LabelPrinter } from "./LabelPrinter"
import type { LabelField, LabelFixedField } from "./LabelPrinter"
import { getSavedStation } from "../utils/printingStation"
import { isHexColor, textColorForBg, getColorName } from "../utils/generateBadgeColor"

interface DynamicPrintModalProps {
  user: DynamicUser
  schema: DynamicSchema
  onClose: () => void
  onPrintSuccess: (user: DynamicUser) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
}

const DynamicPrintModal: React.FC<DynamicPrintModalProps> = ({
  user,
  schema,
  onClose,
  onPrintSuccess,
  showToast,
  cancelCurrentFetch,
}) => {
  const [localUser, setLocalUser] = useState<DynamicUser>(() => JSON.parse(JSON.stringify(user)))
  const [loading, setLoading] = useState(false)
  const [fieldSaving, setFieldSaving] = useState<Record<string, boolean>>({})
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false)
  const isOnline = useNetworkStatus()

  // valor reactivo del color
  const badgeHexRaw = String(SchemaService.getFieldValue(localUser, "badge_color") || "").trim()
  const badgeCategory = String(SchemaService.getFieldValue(localUser, "categoria") || "").trim()
  const badgeHex = badgeHexRaw.toUpperCase()
  const isValidHex = isHexColor(badgeHex)
  const barFg = isValidHex ? textColorForBg(badgeHex) : "#6B7280" /* gray-500 */
  const badgeColorName = isValidHex ? getColorName(badgeHex) : "COLOR NO DEFINIDO"


  // Verificar si es etiqueta genérica (ID 0)
  const isGenericLabel = localUser.id === 0

  // Estado para sistema PATCH - Solo para usuarios reales
  const [originalValues, setOriginalValues] = useState<Record<string, any>>(() => {
    if (isGenericLabel) return {}
    const original: Record<string, any> = {}
    Object.entries(schema).forEach(([fieldName, fieldConfig]) => {
      if (fieldConfig.print && fieldConfig.editable !== false) {
        original[fieldName] = SchemaService.getFieldValue(user, fieldName) ?? ''
      }
    })
    return original
  })

  // Convertir schema dinámico a campos de LabelPrinter
  const convertToLabelFields = useCallback((): LabelField[] => {
    return Object.entries(schema)
      .filter(([_, fieldSchema]) => fieldSchema.print === true)
      .filter(([_, fieldSchema]) => !fieldSchema.fixed) // Excluir campo fixed (va separado)
      .sort((a, b) => (a[1].position || 999) - (b[1].position || 999))
      .map(([fieldName, fieldSchema]) => ({
        name: fieldName,
        label: fieldSchema.label,
        value: SchemaService.getFieldValue(localUser, fieldName)?.toString() || '',
        type: fieldSchema.type === 'number' ? 'number' : 
              fieldSchema.type === 'enum' ? 'enum' : 'text',
        options: fieldSchema.options,
        required: fieldSchema.required,
        editable: isGenericLabel ? true : fieldSchema.editable !== false
      }))
  }, [localUser, schema, isGenericLabel])

  // Obtener campo fijo para QR
  const getFixedField = useCallback((): LabelFixedField | undefined => {
    const fixedFieldName = SchemaService.getFixedField(schema)
    
    if (isGenericLabel) {
      // Para etiquetas genéricas, siempre permitir QR editable
      if (fixedFieldName) {
        const fixedValue = SchemaService.getFieldValue(localUser, fixedFieldName)
        return {
          value: String(fixedValue || 'QR_GENERICO_001'),
          label: schema[fixedFieldName]?.label || 'Código QR',
          editable: true
        }
      } else {
        // Crear campo QR dinámico si no existe en el schema
        return {
          value: 'QR_GENERICO_001',
          label: 'Código QR',
          editable: true
        }
      }
    } else {
      // Para usuarios reales, usar el campo fijo del schema
      if (!fixedFieldName) return undefined
      
      const fixedValue = SchemaService.getFieldValue(localUser, fixedFieldName)
      if (!fixedValue) return undefined

      return {
        value: String(fixedValue),
        label: schema[fixedFieldName]?.label || 'Código QR',
        editable: false // No editable para usuarios reales
      }
    }
  }, [localUser, schema, isGenericLabel])

  // Obtener categoría para watermark
  const getCategoryValue = useCallback((): string => {
    return SchemaService.getFieldValue(localUser, 'categoria') || 
           SchemaService.getFieldValue(localUser, 'category') || 
           ''
  }, [localUser])

  // Verificar si un campo ha cambiado
  const hasFieldChanged = (fieldName: string, currentValue: string): boolean => {
    if (isGenericLabel) return false
    const originalValue = originalValues[fieldName] ?? ''
    return String(currentValue) !== String(originalValue)
  }

  // Guardar cambio individual de campo
  const saveFieldChange = async (fieldName: string, newValue: string) => {
    if (isGenericLabel || !hasFieldChanged(fieldName, newValue)) return
    
    setFieldSaving(prev => ({ ...prev, [fieldName]: true }))
    
    try {
      const uniqueField = SchemaService.getUniqueField(schema)
      if (!uniqueField) {
        throw new Error("No se encontró campo único para la actualización")
      }
      
      const uniqueFieldValue = SchemaService.getFieldValue(user, uniqueField)
      if (!uniqueFieldValue) {
        throw new Error(`Valor del campo único ${uniqueField} no encontrado`)
      }

      // Crear payload PATCH
      const updateData: any = {}
      const mainFields = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']
      
      if (mainFields.includes(fieldName)) {
        updateData[fieldName] = newValue
      } else {
        updateData.extra = { [fieldName]: newValue }
      }

      const updatePayload = {
        uniqueFieldValue: String(uniqueFieldValue),
        updateData
      }

      // Actualización optimista
      const updatedUser: DynamicUser = {
        ...localUser,
        ...(mainFields.includes(fieldName) && { [fieldName]: newValue }),
        ...(updateData.extra && {
          extra: { ...localUser.extra, ...updateData.extra }
        })
      }
      
      setLocalUser(updatedUser)
      await updateDynamicUserInDB(updatedUser)

      // Actualizar originalValues
      setOriginalValues(prev => ({ ...prev, [fieldName]: newValue }))
      setHasUnsyncedChanges(true)

      // Sincronización en segundo plano
      if (isOnline) {
        try {
          await updateDynamicUserByUniqueField(updatePayload)
          showToast("success", `Campo ${fieldName} guardado exitosamente`)
        } catch (networkError) {
          await addToQueue({
            type: "update_dynamic_user",
            data: updatePayload,
            timestamp: Date.now(),
          })
          showToast("info", `Campo ${fieldName} guardado localmente`)
        }
      } else {
        await addToQueue({
          type: "update_dynamic_user",
          data: updatePayload,
          timestamp: Date.now(),
        })
        showToast("info", `Campo ${fieldName} guardado localmente`)
      }
      
    } catch (error) {
      console.error(`Error guardando campo ${fieldName}:`, error)
      showToast("error", `Error al guardar el campo ${fieldName}`)
    } finally {
      setFieldSaving(prev => ({ ...prev, [fieldName]: false }))
    }
  }



  // Manejar impresión
  const handlePrint = useCallback(() => {
    setLoading(true)
    
    try {
      cancelCurrentFetch()
      
      // Validar la estacion seleccionada
      const station = getSavedStation()
      if (!station) {
        setTimeout(() => {
          showToast("error", "Selecciona una estación de impresión antes de imprimir")
          setLoading(false)
        }, 1000)
        return
      }

      // Para etiquetas genéricas, solo imprimir
      if (isGenericLabel) {
        setTimeout(() => {
          showToast("success", "Etiqueta genérica impresa correctamente")
          setLoading(false)
        }, 1000)
        return
      }
      
      // Para usuarios reales, marcar como impreso
      setTimeout(async () => {
        try {
          const uniqueField = SchemaService.getUniqueField(schema)
          if (!uniqueField) {
            throw new Error("No se encontró campo único")
          }
          
          const uniqueFieldValue = SchemaService.getFieldValue(user, uniqueField)
          if (!uniqueFieldValue) {
            throw new Error("Valor del campo único no encontrado")
          }

          const updatedUser: DynamicUser = {
            ...user,
            printed: 1,
            extra: { ...user.extra, printing_stations: String(station) },
          };

          onPrintSuccess(updatedUser)
          await updateDynamicUserInDB(updatedUser)
          onClose()

          const updatePayload = {
            uniqueFieldValue: String(uniqueFieldValue),
            updateData: {
              printed: 1,
              extra: { printing_stations: String(station) },
            },
          };

          // Sincronización en segundo plano
          if (isOnline) {
            try {
              await updateDynamicUserByUniqueField(updatePayload)
              showToast("success", "Gafete impreso y marcado correctamente")
            } catch (networkError) {
              await addToQueue({
                type: "update_dynamic_user",
                data: updatePayload,
                timestamp: Date.now(),
              })
              showToast("info", "Gafete impreso. Se marcará cuando haya conexión")
            }
          } else {
            await addToQueue({
              type: "update_dynamic_user",
              data: updatePayload,
              timestamp: Date.now(),
            })
            showToast("info", "Gafete impreso. Se marcará cuando haya conexión")
          }
          
        } catch (error) {
          console.error('Error marking as printed:', error)
          showToast("error", "Error al marcar como impreso")
          onClose()
        }
      }, 1000)

    } catch (error) {
      console.error('Error during print:', error)
      showToast("error", "Error durante la impresión")
    } finally {
      setLoading(false)
    }
  }, [user, schema, isOnline, onPrintSuccess, onClose, showToast, cancelCurrentFetch, isGenericLabel])

  // Cerrar modal
  const handleCloseModal = useCallback(() => {
    if (hasUnsyncedChanges && !isGenericLabel) {
      onPrintSuccess(localUser)
    }
    onClose()
  }, [hasUnsyncedChanges, isGenericLabel, localUser, onPrintSuccess, onClose])



  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Configurar Impresión de Gafete
              </h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Información del usuario */}
            <div className="mb-6 bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
              <p><strong>Usuario ID:</strong> {localUser.id}</p>
              <p>
                <strong>Estado:</strong>{" "}
                <span
                  className={`py-1 px-2 backdrop-blur-sm text-sm font-medium rounded-xl ${
                    localUser.printed
                      ? "bg-red-600/20 border border-red-500/40 text-red-700"
                      : "bg-gray-600/20 border border-gray-400/30 text-gray-800"
                  }`}
                >
                  {localUser.printed ? "Impreso" : "Sin imprimir"}
                </span>
              </p>
              {!isGenericLabel && (
                <p><strong>Tipo:</strong> Usuario real</p>
              )}
              {isGenericLabel && (
                <p><strong>Tipo:</strong> Etiqueta genérica (reutilizable)</p>
              )}
              <p>
                <strong>Ultima estación de impresión:</strong>{" "}
                {localUser.extra?.printing_stations ? localUser.extra.printing_stations : 'Sin información previa'}
              </p>
            </div>

            {/* Componente LabelPrinter integrado */}
            <LabelPrinter
              fields={convertToLabelFields()}
              fixedField={getFixedField()}
              category={getCategoryValue()}
              size={{ width: 80, height: 28.9, dpi: 300, margin: 2, contentScale: 1.0 }}
              onPrint={handlePrint}
              showControls={true}
              allowEdit={true}
              debug={false}
              className="mb-6"
              onFieldSave={!isGenericLabel ? saveFieldChange : undefined}
              fieldSaving={fieldSaving}
              hasFieldChanged={hasFieldChanged}
              onFieldChange={(fieldName, value) => {
                // Actualizar localUser inmediatamente para reflejar cambios en tiempo real
                const updatedUser = { ...localUser }
                const mainFields = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']
                
                if (mainFields.includes(fieldName)) {
                  updatedUser[fieldName as keyof typeof updatedUser] = value
                } else {
                  updatedUser.extra = { ...updatedUser.extra, [fieldName]: value }
                }
                
                setLocalUser(updatedUser)
              }}
              onFixedFieldChange={(value) => {
                // Actualizar campo fijo/QR en tiempo real
                const updatedUser = { ...localUser }
                const fixedFieldName = SchemaService.getFixedField(schema)
                
                if (fixedFieldName) {
                  const mainFields = ['name', 'last_name', 'email', 'phone_number', 'folio', 'printed', 'print_date', 'number_of_prints']
                  
                  if (mainFields.includes(fixedFieldName)) {
                    updatedUser[fixedFieldName as keyof typeof updatedUser] = value
                  } else {
                    updatedUser.extra = { ...updatedUser.extra, [fixedFieldName]: value }
                  }
                }
                
                setLocalUser(updatedUser)
              }}
              footerSlot={
                <div
                  className={`w-full h-12 rounded-xl flex flex-col items-center justify-center text-sm font-semibold border 
                              ${isValidHex ? "border-gray-300/50" : "border-gray-400"}`}
                  style={{
                    backgroundColor: isValidHex ? badgeHex : "transparent",
                    color: barFg,
                  }}
                  aria-label="Color del gafete"
                  title={isValidHex ? badgeHex : "Sin color válido (#RRGGBB o #RGB)"}
                >
                  <div className="flex flex-wrap items-center justify-center gap-1 text-center">
                    <span className={`${isValidHex ? "text-sm drop-shadow-[0_1px_0_rgba(0,0,0,.15)]" : "text-gray-400"}`}>
                      {isValidHex ? badgeHex : "COLOR NO DEFINIDO"}
                    </span>
                    {isValidHex && (
                      <span className="text-sm text-white-400">-</span>
                    )}
                    {isValidHex && (
                      <span className="text-sm text-white-400">{badgeColorName}</span>
                    )}
                    {badgeCategory && (
                      <span className="text-sm text-white-400">-</span>
                    )}
                    <span className="text-sm text-white-400">{badgeCategory}</span>
                  </div>
                </div>
              }
            />

            {/* Botones de acción */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseModal}
                disabled={loading}
                className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50"
              >
                {isGenericLabel ? 'Cerrar' : 'Cancelar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicPrintModal
